/**
 * Leviathan - Roles List Page
 * Quasar Prime LLC
 *
 * Role management interface
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Plus, Shield, Users, ArrowLeft, Trash2 } from 'lucide-react';

import { API_URL } from '@/config/api';
import { readApiFailure } from '@/utils/formErrors';

interface Role {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  isSystem: boolean;
  isActive: boolean;
  _count: { users: number };
}

export default function Roles() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRoles();
  }, [accessToken]);

  const fetchRoles = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/users/roles/list`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal || !accessToken) return;
    setDeleting(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/users/roles/${deleteModal.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        // data.error is the { code, message, details } OBJECT, so this used to
        // raise an Error whose message was literally "[object Object]".
        const failure = await readApiFailure(response, 'Failed to delete role');
        throw new Error(failure.message);
      }
      setRoles((prev) => prev.filter((r) => r.id !== deleteModal.id));
      setDeleteModal(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete role');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/admin/users"
            className="text-slate-400 hover:text-slate-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="relative">
            <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-500 via-blue-500 to-amber-500" />
            <h1 className="text-3xl font-bold text-slate-100 pl-6">Roles</h1>
            <p className="text-slate-400 text-sm pl-6 mt-1">
              {roles.length} roles - Permission management
            </p>
          </div>
        </div>

        <Link
          to="/admin/roles/new"
          className="flex items-center space-x-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium py-2 px-4 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Create Role</span>
        </Link>
      </div>

      {/* Roles Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
        </div>
      ) : roles.length === 0 ? (
        <div className="text-center py-12 bg-slate-800 border border-slate-700">
          <Shield className="w-12 h-12 mx-auto text-slate-600 mb-4" />
          <div className="text-slate-400 mb-4">No roles defined</div>
          <Link
            to="/admin/roles/new"
            className="text-amber-500 hover:text-amber-400"
          >
            Create your first role &rarr;
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {roles.map((role) => (
            <div
              key={role.id}
              onClick={() => navigate(`/admin/roles/${role.id}/edit`)}
              className="relative bg-slate-800 border border-slate-700 p-5 hover:border-amber-500/50 cursor-pointer transition-colors"
            >
              {/* Corner decorations */}
              <div className="absolute top-0 left-0 w-2 h-2 border-l-2 border-t-2 border-amber-500" />
              <div className="absolute top-0 right-0 w-2 h-2 border-r-2 border-t-2 border-amber-500" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-l-2 border-b-2 border-amber-500" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-r-2 border-b-2 border-amber-500" />

              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-amber-500" />
                  <h3 className="text-lg font-semibold text-slate-100">
                    {role.name}
                  </h3>
                </div>
                <div className="flex items-center space-x-2">
                  {role.isSystem && (
                    <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 border border-blue-500/50">
                      SYSTEM
                    </span>
                  )}
                  {!role.isSystem && role._count.users === 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteModal(role);
                      }}
                      className="p-1 text-slate-500 hover:text-red-400 transition-colors"
                      title="Delete role"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {role.description && (
                <p className="text-slate-400 text-sm mb-4">
                  {role.description}
                </p>
              )}

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2 text-slate-400">
                  <Users className="w-4 h-4" />
                  <span>{role._count.users} users</span>
                </div>
                <span className="text-slate-500">
                  {role.permissions.length} permissions
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 p-6 max-w-md w-full mx-4">
            {/* Corner decorations */}
            <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-red-500" />
            <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-red-500" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-red-500" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-red-500" />

            <h3 className="text-lg font-semibold text-slate-100 mb-4">Delete Role</h3>

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-2 mb-4">
                {error}
              </div>
            )}

            <p className="text-slate-300 mb-6">
              Are you sure you want to delete the role <strong>"{deleteModal.name}"</strong>?
              This action cannot be undone.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setDeleteModal(null);
                  setError(null);
                }}
                className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium transition-colors disabled:opacity-50"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
